import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { RoutesList } from '../config/routes-list';
import { attemptChunkReload } from '../services/pwa/chunk-error-reloader';

const IT_FALLBACKS: Record<string, string> = {
  'routeError.title': 'Si è verificato un errore',
  'routeError.subtitle':
    'Qualcosa è andato storto durante il caricamento di questa pagina. Riprova oppure torna alla home.',
  'routeError.reload': 'Ricarica',
  'routeError.goHome': 'Vai alla home',
};

export function RouteErrorScreen() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const tr = (key: string, options?: Record<string, unknown>): string => {
    const translated = t(key, options ?? {});
    if (translated === key) {
      return IT_FALLBACKS[key] ?? key;
    }
    return translated;
  };

  useEffect(() => {
    if (error) {
      if (attemptChunkReload(error)) {
        return;
      }
      console.error('[RouteErrorScreen]', error);
    }
  }, [error]);

  const statusCode = isRouteErrorResponse(error) ? error.status : null;

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    try {
      navigate(RoutesList.HOME, { replace: true });
    } catch {
      window.location.assign(RoutesList.HOME);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 4,
      }}
    >
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          maxWidth: 400,
          width: '100%',
        }}
      >
        <ErrorOutlinedIcon sx={{ fontSize: 64, color: '#0F172A' }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {tr('routeError.title')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          {tr('routeError.subtitle')}
        </Typography>
        {statusCode !== null && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              textAlign: 'center',
            }}
          >
            {t('routeError.statusLabel', { status: statusCode })}
          </Typography>
        )}
        <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleReload}>
          {tr('routeError.reload')}
        </Button>
        <Button variant="text" fullWidth onClick={handleGoHome}>
          {tr('routeError.goHome')}
        </Button>
      </Stack>
    </Box>
  );
}
